import { useState } from "react";
import { isGifMediaUrl, type Embed, type GifAttachment } from "@atrium/shared";
import { useMotionPrefs } from "../../lib/prefs";
import { IconPlay } from "../../components/icons";

/** GIF embeds that may render inline: from an allow-listed provider host. */
export const inlineGifs = (embeds: Embed[] | undefined) => (embeds ?? []).filter((e) => e.type === "gif" && isGifMediaUrl(e.image?.url ?? e.url));

/**
 * A sent GIF, shown as the image itself. Plays automatically unless GIF
 * autoplay or motion is turned off, in which case it shows the first frame
 * (or a placeholder) until pressed.
 */
export function ChatGif({ embed }: { embed: Embed }) {
  const { gifAutoplay } = useMotionPrefs();
  const [play, setPlay] = useState(false);
  const animated = embed.image?.url ?? embed.url!;
  const still = embed.thumbnail?.url && embed.thumbnail.url !== animated && isGifMediaUrl(embed.thumbnail.url) ? embed.thumbnail.url : null;
  const playing = gifAutoplay || play;
  const w = embed.image?.width ?? 320;
  const h = embed.image?.height ?? 240;
  // Fit within 400×300 without upscaling, keeping the aspect ratio.
  const scale = Math.min(1, 400 / w, 300 / h);
  const style = { width: Math.round(w * scale), aspectRatio: `${w} / ${h}` };
  const title = embed.title || "GIF";
  return (
    <div className="relative mt-1 max-w-full overflow-hidden rounded-lg bg-surface-2" style={style}>
      {playing || still ? (
        <img src={playing ? animated : still!} alt={title} title={title} loading="lazy" draggable={false} className="block size-full object-cover" />
      ) : (
        <span className="sr-only">{title}</span>
      )}
      {!playing && (
        <button type="button" onClick={() => setPlay(true)} aria-label={`Play GIF: ${title}`} className="absolute inset-0 flex items-center justify-center bg-black/25 text-white">
          <IconPlay className="icon size-8" aria-hidden />
        </button>
      )}
      <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/65 px-1 text-[11px] font-semibold text-white" aria-hidden>
        GIF
      </span>
    </div>
  );
}

/** Same shape the server stores, for the optimistic copy of a GIF message. */
export function gifEmbed(gif: GifAttachment): Embed {
  return { type: "gif", url: gif.url, title: gif.title || undefined, image: { url: gif.url, width: gif.width, height: gif.height }, thumbnail: { url: gif.stillUrl ?? gif.previewUrl } };
}
